import { createRouter, createWebHistory } from "vue-router";
import NotFound from "../views/NotFound.vue";
import { i18n } from "../languages/i18n";
import store from "../store/index";
import { jwtDecode } from "jwt-decode";
import { slotRoutes } from "../slot/routes";
import { liveCasinoRoutes } from "../livecasino/routes";
import { instantRoutes } from "../instant/routes";
import {
  isCategoryAllowed,
  getFirstAllowedSection,
  fetchAppConfiguration,
  appConfigState,
} from "../helpers/appConfiguration";

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes: [
    {
      path: "/:lang?/verifytoken/:token",
      name: "VerifyToken",
      beforeEnter(to, from, next) {
        const token = to.params.token;
        const redirectUrl = to.query.redirectUrl;

        if (token) {
          localStorage.setItem("botokentheear", token);
          if (redirectUrl) window.location.href = redirectUrl;
          else next("/");
        } else {
          next("/");
        }
      },
    },
    // Root redirect to allowed section
    {
      path: "/:lang?/:skinFilt(\\d+)?",
      redirect: (to) => {
        const lang = to.params.lang || localStorage.getItem("locale") || "en";
        const skin = to.params.skinFilt;
        const targetSection = getFirstAllowedSection();
        return skin ? `/${lang}/${skin}/${targetSection}` : `/${lang}/${targetSection}`;
      },
    },
    // Module routes
    ...slotRoutes,
    ...liveCasinoRoutes,
    ...instantRoutes,
    // Backward compatibility redirects for legacy /:lang/:skinFilt?/xyz URLs
    {
      path: "/:lang/:skinFilt(\\d+)?/freebets/:pathMatch(.*)*",
      redirect: (to) => {
        const rest = to.params.pathMatch ? `/${to.params.pathMatch}` : "";
        const skin = to.params.skinFilt ? `/${to.params.skinFilt}` : "";
        return `/${to.params.lang}${skin}/slot/freebets${rest}`;
      },
    },
    {
      path: "/:lang/:skinFilt(\\d+)?/rains/:pathMatch(.*)*",
      redirect: (to) => {
        const rest = to.params.pathMatch ? `/${to.params.pathMatch}` : "";
        const skin = to.params.skinFilt ? `/${to.params.skinFilt}` : "";
        return `/${to.params.lang}${skin}/slot/rains${rest}`;
      },
    },
    {
      path: "/:lang/:skinFilt(\\d+)?/challenges/:pathMatch(.*)*",
      redirect: (to) => {
        const rest = to.params.pathMatch ? `/${to.params.pathMatch}` : "";
        const skin = to.params.skinFilt ? `/${to.params.skinFilt}` : "";
        return `/${to.params.lang}${skin}/slot/challenges${rest}`;
      },
    },
    {
      path: "/:lang/:skinFilt(\\d+)?/export",
      redirect: (to) => {
        const skin = to.params.skinFilt ? `/${to.params.skinFilt}` : "";
        return `/${to.params.lang}${skin}/slot/export`;
      },
    },
    {
      path: "/:lang/:skinFilt(\\d+)?/chat",
      redirect: (to) => {
        const skin = to.params.skinFilt ? `/${to.params.skinFilt}` : "";
        return `/${to.params.lang}${skin}/slot/chat`;
      },
    },
    {
      path: "/:lang/:skinFilt(\\d+)?/players/:pathMatch(.*)*",
      redirect: (to) => {
        const rest = to.params.pathMatch ? `/${to.params.pathMatch}` : "";
        const skin = to.params.skinFilt ? `/${to.params.skinFilt}` : "";
        return `/${to.params.lang}${skin}/slot/players${rest}`;
      },
    },
    {
      path: "/:lang?/404",
      name: "NotFoundPage",
      component: NotFound,
      meta: { requiresAuth: true },
    },
    {
      path: "/:pathMatch(.*)*",
      redirect: "/404",
      name: "NotFound",
      meta: { requiresAuth: true },
    },
  ],
  scrollBehavior(to, from, savedPosition) {
    if (to.path === from.path) {
      return false;
    }
    return { top: 0, behavior: "smooth" };
  },
});

function checkOverlay(to) {
  const params = new URLSearchParams(window.location.search);
  const skinFromQuery = params.get("skin_id");
  const skinFromParam = to?.params?.skinFilt;
  const skinFromStore = store.state.skinStorage;

  const validSkin =
    skinFromQuery ||
    (skinFromParam && skinFromParam !== "all" ? skinFromParam : null) ||
    (skinFromStore && skinFromStore !== "all" ? skinFromStore : null);

  return !validSkin;
}

function checkLanguageParam(to) {
  const supportedLangs = ["en"];
  const lang = supportedLangs.includes(to.params.lang)
    ? to.params.lang
    : supportedLangs.includes(localStorage.getItem("locale"))
      ? localStorage.getItem("locale")
      : "en";

  if (localStorage.getItem("locale") !== lang) {
    localStorage.setItem("locale", lang);
    i18n.global.locale.value = lang;
  }

  return lang;
}

router.beforeEach(async (to, from, next) => {
  if (!appConfigState.isLoaded) {
    try {
      await fetchAppConfiguration();
    } catch (e) {
      console.error("Failed to load initial configuration:", e);
    }
  }

  const lang = checkLanguageParam(to);

  // FORZARE SEMPRE LA LINGUA (solo se definita e diversa)
  if (to.name && to.params.lang && to.params.lang !== lang) {
    return next({
      name: to.name,
      params: { ...to.params, lang },
      query: to.query,
    });
  }

  // Check category section permissions
  const targetSection =
    to.meta?.section ||
    (to.path.includes("/slot")
      ? "slot"
      : to.path.includes("/livecasino")
        ? "livecasino"
        : to.path.includes("/instant")
          ? "instant"
          : null);

  if (targetSection && !isCategoryAllowed(targetSection)) {
    const allowedSection = getFirstAllowedSection();
    if (allowedSection && allowedSection !== targetSection) {
      const skin = to.params.skinFilt;
      const redirectPath = skin
        ? `/${lang}/${skin}/${allowedSection}`
        : `/${lang}/${allowedSection}`;
      if (to.path !== redirectPath && to.fullPath !== redirectPath) {
        return next(redirectPath);
      }
    }
  }

  const token = localStorage.getItem("botokentheear");
  let isAdmin = false;
  let firstSkinKey = null;
  let allSkins = [];

  if (token) {
    try {
      const decoded = jwtDecode(token);
      isAdmin = decoded.is_admin;
      const userId = decoded.user_id;

      if (!isAdmin && decoded.skins) {
        allSkins = Object.values(decoded.skins).map((s) => s.toString());
        firstSkinKey = allSkins[0];
      }

      store.commit("setUserId", userId);
      store.commit("setIsAdmin", isAdmin);
    } catch (error) {
      console.error("Errore nel decodificare il token:", error);
    }
  }

  // Se la rotta ha il parametro skinFilt numerico
  if (to.params.skinFilt) {
    let currentSkin = to.params.skinFilt;

    if (!isAdmin && allSkins.length > 0) {
      if (!allSkins.includes(currentSkin.toString())) {
        currentSkin = firstSkinKey;
        if (to.name && currentSkin !== to.params.skinFilt) {
          return next({
            name: to.name,
            params: { ...to.params, lang, skinFilt: currentSkin },
            query: to.query,
          });
        }
      }
    }

    store.commit("setSkinID", parseInt(currentSkin) || currentSkin);
  } else {
    // Nessun skinFilt nella rotta
    const activeSkin =
      (!isAdmin && firstSkinKey) ||
      (store.state.skinStorage && store.state.skinStorage !== "all"
        ? store.state.skinStorage
        : null);

    const routesWithoutSkin = [
      "VerifyToken",
      "NotFoundPage",
      "NotFound",
      "LoyaltyCoins",
      "LoyaltyCoinsCreate",
      "LoyaltyCoinsEdit",
    ];

    if (
      activeSkin &&
      to.name &&
      !routesWithoutSkin.includes(to.name) &&
      to.params.skinFilt !== activeSkin.toString()
    ) {
      return next({
        name: to.name,
        params: { ...to.params, lang, skinFilt: activeSkin.toString() },
        query: to.query,
      });
    }

    if (
      !isAdmin &&
      firstSkinKey &&
      to.name !== "NotFoundPage" &&
      to.name !== "NotFound" &&
      to.name !== "VerifyToken" &&
      to.params.skinFilt !== firstSkinKey
    ) {
      return next({
        name: to.name,
        params: { ...to.params, lang, skinFilt: firstSkinKey },
        query: to.query,
      });
    }
    store.commit("setSkinID", null);
  }

  if (
    to.matched.some((r) => r.meta && typeof r.meta.showOverlay !== "undefined")
  ) {
    to.meta.showOverlay = checkOverlay(to);
  }

  const playerLogged = localStorage.getItem("botokentheear") ?? false;
  const pageAccess = to.meta.requiresAuth ?? false;

  if (pageAccess && !playerLogged) {
    let url = window.location.href;
    let prefix = process.env.VUE_APP_ENVIRONMENT === "stage" ? "stage-" : "";
    window.location.href =
      "https://" +
      prefix +
      "framebtob.theearplatform.com/login?module=theearcasino&moduleUrl=" +
      url;
  } else if (!pageAccess && playerLogged) {
    const allowed = getFirstAllowedSection();
    const defaultRouteName =
      allowed === "livecasino"
        ? "LiveCasinoHome"
        : allowed === "instant"
          ? "InstantHome"
          : "Home";
    if (to.name !== defaultRouteName) {
      return next({ name: defaultRouteName, params: { lang } });
    }
    return next();
  } else {
    next();
  }
});

export default router;
